import { usePromoSliderPosts } from "./usePromoSliderPosts";
import { PostsPromoSlider } from "./PostsPromoSlider";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TippspielLinkoutButton } from "@shared/Components/Tippspiel/TippspileLinkoutButton/TippspielLinkoutButton";
import { usePostListModalStore } from "../../Components/Modals/PostListModal/PostListModalStore";
import { usePostCardClickHandler } from "../GroupDetailsPage/Hooks/usePostCardClickHandler";
import { usePostFormModalStore } from "../GroupDetailsPage/Stores/PostFormModalStore";
import {
    POST_SOURCE_TYPE,
    POST_FORM_CONTENT_INPUT,
} from "../../Components/Post/PostForm/PostFormModal.types";
import { usePostDetailsNavigationActions } from "@infrastructure/Hooks/usePostDetailsNavigationActions";
import { PostDetailsPageActions } from "../../Components/Modals/PostDetailsModal/PostDetailsModal";
import {
    POST_LIST_TYPES,
    PostComponentOptionsType,
    PostType,
} from "../../Components/Post/Post.types";
import { publish } from "@infrastructure/Events/EventBus";
import {
    POST_DETAILS_CHANGED,
    POST_OPTIONS_CLICK,
} from "../../Components/Post/PostForm/events";
import { SERVICE_MODALS_OPTIONS_STATES } from "../../Components/Modals/ServiceFunctionsModal.types";
import { PostsPromoFinalSlide } from "./PostsPromoFinalSlide";
import { PostsPromoSliderItem } from "./PostsPromoSliderItem";
import { ButtonContainer } from "./PostsPromoSlider.styles";
import { OnReactionEvent } from "../../Components/Reactions/Events/OnReactionEvent.types";
import {
    addUserReactionToObject,
    deleteUserReactionOnObject,
    updateUserReactionOnObject,
} from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../../Components/ReactionsTooltip/ReactionActionState";
import { useOnPostReaction } from "../../Components/Reactions/Hooks/useOnPostReaction";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { AxiosError } from "axios";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import {
    ErrorState,
    useErrorStore,
} from "../../Components/Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import {
    fetcherReactToPost,
    fetcherRemoveReactFromPost,
} from "../../DataFetchers/PostDataFetcher";
import { ReactionResponse } from "@shared/Types/Reactions/ReactionTypes.types";
import { useInterruptableAction } from "@infrastructure/Persistence/UserState";

export function PostsPromoSliderFacade() {
    const { posts } = usePromoSliderPosts();
    const { setIsPostListModalOpen } = usePostListModalStore();
    const onPostCardClick = usePostCardClickHandler(true);

    const [postList, setPostList] = useState<PostType[]>(posts);

    useEffect(() => {
        setPostList(posts);
    }, [posts]);

    const { setIsPostFormModalOpen } = usePostFormModalStore();

    const openPostFormHandler = useCallback(
        (sourceType?: POST_SOURCE_TYPE) => {
            setIsPostFormModalOpen(
                true,
                POST_FORM_CONTENT_INPUT.DEFAULT,
                sourceType,
            );
        },
        [setIsPostFormModalOpen],
    );

    useEffect(() => {
        return () => {
            setIsPostFormModalOpen(false);
        };
    }, [setIsPostFormModalOpen]);

    const { navigateToPostWithAction } = usePostDetailsNavigationActions();

    const commentButtonClicked = useCallback(
        (e: React.MouseEvent<HTMLElement>, postId: number) => {
            e.stopPropagation();
            navigateToPostWithAction(
                postId,
                PostDetailsPageActions.WRITE_COMMENT,
            );
        },
        [navigateToPostWithAction],
    );

    const { setReactionActionState } = useReactionActionStore();
    const { setErrorState } = useErrorStore();

    const onPostReaction = useCallback(
        (event: OnReactionEvent) => {
            updateUserReactionOnObject(
                event.entityId.toString(),
                event.reactions,
                ReactionableObjectEnum.POST,
            );
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        },
        [setReactionActionState],
    );

    useOnPostReaction(setPostList, onPostReaction);
    const [postActionInProgress, setPostActionInProgress] = useState<any>();

    const createReactOnPost =
        (postId: number) => (reactionTypeName: string, action: string) => {
            resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.REACT_ON_POST);
            setReactionActionState(ReactionActionState.LOADING);
            setPostActionInProgress({
                reactionTypeName: reactionTypeName,
                action: action,
                postId: postId,
            });
            performReactionOnPost(reactionTypeName, action, postId);
        };

    const performReactionOnPost = (
        reactionTypeName: string,
        action: string,
        postId: number,
    ) => {
        const updatedPosts = JSON.parse(JSON.stringify(postList));
        const postIndex = updatedPosts.findIndex(
            (item: PostType) => item.id.toString() === postId.toString(),
        );
        if (postIndex === -1) {
            return;
        }
        const catchError = (err: AxiosError) => {
            switch (err.response?.status) {
                case RESPONSE_CODE_RESOURCE_SUSPENDED:
                case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                    setErrorState(ErrorState.POST_SUSPENDED);
                    break;
                case RESPONSE_CODE_PROFILE_SUSPENDED:
                    setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                    break;
                case RESPONSE_CODE_LIMIT_EXCEEDED:
                    setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                    break;
            }
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        };
        let reactionFetcher;

        switch (action) {
            case "create":
                reactionFetcher = fetcherReactToPost;
                break;
            case "delete":
                reactionFetcher = fetcherRemoveReactFromPost;
                break;
            default:
                return;
        }
        reactionFetcher(postId, reactionTypeName)
            .then((res: ReactionResponse) => {
                if (action === "create") {
                    addUserReactionToObject(
                        postId.toString(),
                        reactionTypeName,
                        ReactionableObjectEnum.POST,
                    );
                } else {
                    deleteUserReactionOnObject(
                        postId.toString(),
                        ReactionableObjectEnum.POST,
                    );
                }
                updatedPosts[postIndex].postData.postReactions = res.reactions;

                setPostActionInProgress(undefined);
                setTimeout(
                    () => setReactionActionState(ReactionActionState.IDLE),
                    SWITCHING_STATE_TO_IDLE_TIME,
                );
                publish({
                    name: POST_DETAILS_CHANGED,
                    data: { ...updatedPosts[postIndex] },
                });
            })
            .catch(catchError);
    };

    const cancelActionInProgress = () => {
        setPostActionInProgress(undefined);
    };

    useInterruptableAction(
        postActionInProgress,
        () =>
            performReactionOnPost(
                postActionInProgress.reactionTypeName,
                postActionInProgress.action,
                postActionInProgress.postId,
            ),
        cancelActionInProgress,
    );

    const postSlides = useMemo(
        () =>
            postList.length > 0
                ? [
                      ...postList.map((post: PostType) => {
                          const postOptions: PostComponentOptionsType = {
                              isInsidePostDetails: false,
                              updateFromServer: (postId: number) => {},
                              id: post.id,
                              listType: POST_LIST_TYPES.PROMO,
                              data: post,
                              showGroupName: false,
                              postActions: {
                                  onAvatarClick: () => {},
                                  onCommentButtonClick: commentButtonClicked,
                                  onCardContentClickHandler: onPostCardClick,
                                  isInsidePostDetails: false,
                                  openPostFormHandler,
                                  createReactOnPost,
                                  showCommentTeaser: false,
                                  setIsPostOptionsModalOpen: () => {},
                                  setModalCurrentAction: () => {},
                                  setSelectedPost: (post) =>
                                      publish({
                                          name: POST_OPTIONS_CLICK,
                                          post,
                                          modalState:
                                              SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS,
                                      }),
                              },
                          };
                          return (
                              <PostsPromoSliderItem
                                  key={post.id}
                                  {...postOptions}
                              />
                          );
                      }),
                      <PostsPromoFinalSlide
                          users={postList.map(({ user }) => user)}
                      />,
                  ]
                : [],
        [commentButtonClicked, onPostCardClick, openPostFormHandler, postList],
    );

    return (
        <>
            {postSlides.length > 0 && <PostsPromoSlider slides={postSlides} />}
            <ButtonContainer>
                <TippspielLinkoutButton
                    text="zur Community"
                    onClick={() => setIsPostListModalOpen(true)}
                />
            </ButtonContainer>
        </>
    );
}
